/**
 * VoiceOfUserCard — Master Admin (Feb 2026)
 * ==========================================
 *
 * Two-pane card that lives in the Command Centre:
 *   • Top pane: Ideas Inbox — teen/adult-submitted ideas, sortable by
 *     status, with inline status changes + "Award NFT" toggle.
 *   • Bottom pane: Top 25 unmatched dashboard searches over the last
 *     7 days — a free product-roadmap signal.
 *
 * Only the Master Admin (robert@hibff.com) can edit; other admins see
 * read-only data. The 403 enforcement happens server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Lightbulb, Search as SearchIcon, Award, Loader2, RefreshCw, ChevronDown, Sparkles,
  Rocket, X, CheckCircle2,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const STATUS_OPTIONS = [
  { value: 'new',       label: 'New',       color: '#2563EB' },
  { value: 'reviewing', label: 'Reviewing', color: '#F59E0B' },
  { value: 'accepted',  label: 'Accepted',  color: '#16A34A' },
  { value: 'rejected',  label: 'Rejected',  color: '#9CA3AF' },
  { value: 'shipped',   label: 'Shipped',   color: '#7B4FD8' },
];

const statusBadge = (status) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white"
      style={{ background: opt.color }}
    >
      {opt.label}
    </span>
  );
};

const VoiceOfUserCard = () => {
  const [tab, setTab] = useState('ideas');
  const [ideas, setIdeas] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  // AI clustering state
  const [clusters, setClusters] = useState(null);
  const [clusterModel, setClusterModel] = useState('');
  const [clusterGeneratedAt, setClusterGeneratedAt] = useState('');
  const [clustering, setClustering] = useState(false);
  // Draft-card flow state
  const [draftingCluster, setDraftingCluster] = useState(null); // the cluster being drafted (loading)
  const [draftResult, setDraftResult] = useState(null);          // backend response on success

  const loadIdeas = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const data = await apiClient.request(`/api/admin/ideas${qs}`);
      setIdeas(data?.ideas || []);
      setCounts(data?.counts || {});
    } catch (e) {
      setError(e?.message || 'Could not load ideas inbox.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadUnmatched = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/admin/search/unmatched?days=7&limit=25');
      setUnmatched(data?.queries || []);
    } catch {
      // Soft-fail — unmatched is non-critical.
    }
  }, []);

  useEffect(() => {
    loadIdeas();
    loadUnmatched();
  }, [loadIdeas, loadUnmatched]);

  const patchIdea = async (ideaId, body) => {
    setSavingId(ideaId);
    try {
      const data = await apiClient.request(`/api/admin/ideas/${ideaId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (data?.success) {
        setIdeas((prev) => prev.map((i) => (i.id === ideaId ? data.idea : i)));
        // Refresh counts after status changes
        loadIdeas();
      }
    } catch (e) {
      setError(e?.message || 'Could not update idea.');
    } finally {
      setSavingId(null);
    }
  };

  const runCluster = async () => {
    setClustering(true);
    setError('');
    try {
      const data = await apiClient.request('/api/admin/ideas/cluster', { method: 'POST' });
      setClusters(data?.clusters || []);
      setClusterModel(data?.model || '');
      setClusterGeneratedAt(data?.generated_at || '');
      setTab('clusters');
    } catch (e) {
      setError(e?.message || 'Cluster run failed — try again in a moment.');
    } finally {
      setClustering(false);
    }
  };

  const draftCard = async (cluster) => {
    setDraftingCluster(cluster);
    setDraftResult(null);
    setError('');
    try {
      const data = await apiClient.request('/api/admin/ideas/draft-card', {
        method: 'POST',
        body: JSON.stringify({
          theme: cluster.theme,
          summary: cluster.summary || '',
          rationale: cluster.rationale || '',
          idea_ids: cluster.idea_ids || [],
        }),
      });
      if (data?.success) {
        setDraftResult(data);
        // Refresh ideas + clusters so accepted+NFT-awarded states sync.
        loadIdeas();
      } else {
        setError('Draft failed — try again.');
      }
    } catch (e) {
      setError(e?.message || 'Draft failed — try again.');
    } finally {
      setDraftingCluster((c) => (c === cluster ? null : c));
    }
  };

  const filterChips = useMemo(() => ([
    { value: '',          label: `All (${counts.all || 0})` },
    { value: 'new',       label: `New (${counts.new || 0})` },
    { value: 'reviewing', label: `Reviewing (${counts.reviewing || 0})` },
    { value: 'accepted',  label: `Accepted (${counts.accepted || 0})` },
    { value: 'shipped',   label: `Shipped (${counts.shipped || 0})` },
    { value: 'rejected',  label: `Rejected (${counts.rejected || 0})` },
  ]), [counts]);

  return (
    <div
      className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-gray-200"
      data-testid="voice-of-user-card"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb size={18} style={{ color: '#F5831F' }} />
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
              Voice of User
            </span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">
            Ideas Inbox &amp; Missing Searches
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            What teens and adults are asking for that we don&apos;t yet offer.
            <span className="ml-1" style={{ color: '#7B4FD8' }}>
              🏆 Chosen ideas earn an NFT.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={runCluster}
          disabled={clustering}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #7B4FD8 0%, #5B21B6 100%)' }}
          data-testid="vou-cluster-btn"
          title="Group similar ideas with Claude and prioritise net-new themes"
        >
          {clustering ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {clustering ? 'Clustering…' : 'AI Cluster Ideas'}
        </button>
        <button
          type="button"
          onClick={() => { loadIdeas(); loadUnmatched(); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-600 hover:bg-gray-100"
          data-testid="vou-refresh"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
        </div>
      </div>

      {/* Tab switch */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab('ideas')}
          className="px-3 py-2 text-sm font-semibold border-b-2 transition"
          style={{
            borderColor: tab === 'ideas' ? '#F5831F' : 'transparent',
            color: tab === 'ideas' ? '#F5831F' : '#6B7280',
          }}
          data-testid="vou-tab-ideas"
        >
          <Lightbulb size={14} className="inline -mt-0.5 mr-1" />
          Ideas ({counts.all || 0})
        </button>
        <button
          type="button"
          onClick={() => setTab('unmatched')}
          className="px-3 py-2 text-sm font-semibold border-b-2 transition"
          style={{
            borderColor: tab === 'unmatched' ? '#7B4FD8' : 'transparent',
            color: tab === 'unmatched' ? '#7B4FD8' : '#6B7280',
          }}
          data-testid="vou-tab-unmatched"
        >
          <SearchIcon size={14} className="inline -mt-0.5 mr-1" />
          Missing Searches ({unmatched.length})
        </button>
        {clusters && (
          <button
            type="button"
            onClick={() => setTab('clusters')}
            className="px-3 py-2 text-sm font-semibold border-b-2 transition"
            style={{
              borderColor: tab === 'clusters' ? '#5B21B6' : 'transparent',
              color: tab === 'clusters' ? '#5B21B6' : '#6B7280',
            }}
            data-testid="vou-tab-clusters"
          >
            <Sparkles size={14} className="inline -mt-0.5 mr-1" />
            AI Clusters ({clusters.length})
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs" data-testid="vou-error">
          {error}
        </div>
      )}

      {tab === 'ideas' ? (
        <>
          {/* Status filter chips */}
          <div className="flex gap-1.5 flex-wrap mb-3">
            {filterChips.map((c) => (
              <button
                key={c.value || 'all'}
                type="button"
                onClick={() => setStatusFilter(c.value)}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition"
                style={{
                  background: statusFilter === c.value ? '#F5831F' : '#F3F4F6',
                  color: statusFilter === c.value ? 'white' : '#374151',
                }}
                data-testid={`vou-filter-${c.value || 'all'}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Ideas list */}
          {loading ? (
            <div className="py-8 flex items-center justify-center text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : ideas.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400" data-testid="vou-no-ideas">
              No ideas yet — share the dashboard prompt with your users.
            </div>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {ideas.map((idea) => (
                <div
                  key={idea.id}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-3 hover:bg-white transition"
                  data-testid={`vou-idea-${idea.id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                    <div className="flex items-center gap-2 text-[11px]">
                      {statusBadge(idea.status)}
                      <span className="font-semibold text-gray-700">{idea.user_name || idea.user_email}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{idea.age_band}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{idea.created_at?.slice(0, 10)}</span>
                      {idea.nft_awarded && (
                        <span className="inline-flex items-center gap-0.5 text-purple-600 font-bold">
                          <Award size={12} /> NFT
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed mb-2 whitespace-pre-wrap">
                    {idea.idea_text}
                  </p>
                  {idea.source_query && (
                    <div className="text-[10px] text-gray-400 italic mb-2">
                      Sparked by search: &ldquo;{idea.source_query}&rdquo;
                    </div>
                  )}

                  {/* Admin controls */}
                  <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200 mt-2">
                    <div className="relative">
                      <select
                        value={idea.status}
                        onChange={(e) => patchIdea(idea.id, { status: e.target.value })}
                        disabled={savingId === idea.id}
                        className="appearance-none pl-2.5 pr-7 py-1 rounded-full text-[11px] font-semibold border border-gray-300 bg-white text-gray-700 cursor-pointer"
                        data-testid={`vou-status-${idea.id}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="absolute right-2 top-1.5 pointer-events-none text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => patchIdea(idea.id, { nft_awarded: !idea.nft_awarded })}
                      disabled={savingId === idea.id}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold transition"
                      style={{
                        background: idea.nft_awarded ? '#7B4FD8' : '#F3F4F6',
                        color: idea.nft_awarded ? 'white' : '#374151',
                      }}
                      data-testid={`vou-nft-${idea.id}`}
                    >
                      <Award size={11} />
                      {idea.nft_awarded ? 'NFT Awarded' : 'Award NFT'}
                    </button>
                    {savingId === idea.id && (
                      <Loader2 size={13} className="animate-spin text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : tab === 'unmatched' ? (
        <div data-testid="vou-unmatched-list">
          {unmatched.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              No unmatched searches in the last 7 days. Everyone is finding what they need.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <th className="py-2 pl-1">#</th>
                  <th className="py-2">Query</th>
                  <th className="py-2 text-right">Times</th>
                  <th className="py-2 text-right pr-1">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map((u, i) => (
                  <tr key={u.query} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pl-1 text-gray-400 text-xs">{i + 1}</td>
                    <td className="py-2 font-medium text-gray-800">&ldquo;{u.query}&rdquo;</td>
                    <td className="py-2 text-right font-bold text-gray-700">{u.count}</td>
                    <td className="py-2 text-right pr-1 text-xs text-gray-500">{u.last_seen?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // Clusters tab
        <div data-testid="vou-clusters">
          {clusterGeneratedAt && (
            <div className="text-[10px] text-gray-400 mb-3">
              Generated {clusterGeneratedAt.slice(0, 19).replace('T', ' ')} UTC · model {clusterModel || 'n/a'}
            </div>
          )}
          {!clusters || clusters.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              No clusters yet. Hit &ldquo;AI Cluster Ideas&rdquo; above to generate themes.
            </div>
          ) : (
            <div className="space-y-2.5 max-h-[480px] overflow-y-auto pr-1">
              {clusters.map((c, i) => {
                const prio = (c.priority || 'LOW').toUpperCase();
                const prioStyle = prio === 'HIGH'
                  ? { bg: '#FEE2E2', fg: '#B91C1C', dot: '#DC2626' }
                  : prio === 'MEDIUM'
                    ? { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' }
                    : { bg: '#F3F4F6', fg: '#374151', dot: '#9CA3AF' };
                const ids = c.idea_ids || [];
                return (
                  <div
                    key={`${c.theme}-${i}`}
                    className="rounded-xl border border-gray-200 bg-white p-3.5 hover:shadow-sm transition"
                    data-testid={`vou-cluster-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: prioStyle.bg, color: prioStyle.fg }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: prioStyle.dot }} />
                          {prio}
                        </span>
                        <h4 className="font-bold text-gray-900 text-sm leading-snug">
                          {c.theme}
                        </h4>
                        <span className="text-[10px] text-gray-500">· {ids.length} idea{ids.length !== 1 ? 's' : ''}</span>
                      </div>
                      {c.duplicates_existing_card && (
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: '#F3E8FF', color: '#7B4FD8' }}
                          title="This cluster duplicates an existing dashboard feature"
                        >
                          duplicates: {c.duplicates_existing_card}
                        </span>
                      )}
                      {c.is_new_idea && !c.duplicates_existing_card && (
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: '#DCFCE7', color: '#15803D' }}
                          title="Net-new idea — no equivalent on the dashboard yet"
                        >
                          ✨ NET-NEW
                        </span>
                      )}
                    </div>
                    {c.summary && (
                      <p className="text-sm text-gray-700 leading-relaxed mb-1.5">{c.summary}</p>
                    )}
                    {c.rationale && (
                      <p className="text-[11px] text-gray-500 italic mb-2">{c.rationale}</p>
                    )}
                    {ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {ids.slice(0, 12).map((id) => (
                          <span
                            key={id}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
                            title={id}
                          >
                            {id.slice(0, 8)}
                          </span>
                        ))}
                        {ids.length > 12 && (
                          <span className="text-[10px] text-gray-400">+{ids.length - 12} more</span>
                        )}
                      </div>
                    )}
                    {/* Feb 2026 — one-click Draft This Card. Only visible
                        for net-new clusters; LOW duplicates can't be
                        drafted because they already exist. */}
                    {!c.duplicates_existing_card && (
                      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => draftCard(c)}
                          disabled={!!draftingCluster}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white shadow-sm disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #F5831F 0%, #DC2626 100%)' }}
                          data-testid={`vou-draft-card-${i}`}
                          title="Scaffold a React component + PRD bullet into ROADMAP.md, accept all ideas in this cluster, and mint a Voice-of-HiBFF NFT into every contributor's vault."
                        >
                          {draftingCluster === c
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Rocket size={12} />}
                          {draftingCluster === c ? 'Drafting…' : `Draft Card + Award ${ids.length} NFT${ids.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Draft Card success modal */}
      {draftResult && (
        <div
          className="fixed inset-0 z-[9700] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDraftResult(null)}
          data-testid="draft-result-backdrop"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-6 sm:p-7 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="draft-result-modal"
          >
            <button
              type="button"
              onClick={() => setDraftResult(null)}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
              data-testid="draft-result-close"
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={20} style={{ color: '#16A34A' }} />
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                Drafted &amp; Awarded
              </span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 leading-snug">
              {draftResult?.scaffold?.card_title || 'New card drafted'}
            </h3>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl bg-orange-50 p-3 text-center">
                <div className="text-2xl font-black text-orange-600">{draftResult.ideas_processed}</div>
                <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">Ideas accepted</div>
              </div>
              <div className="rounded-xl bg-purple-50 p-3 text-center">
                <div className="text-2xl font-black text-purple-600">{draftResult.nfts_minted}</div>
                <div className="text-[10px] uppercase tracking-wider text-purple-700 font-bold">NFTs minted</div>
              </div>
              <div className="rounded-xl bg-green-50 p-3 text-center">
                <div className="text-2xl font-black text-green-600">{(draftResult.notified_user_ids || []).length}</div>
                <div className="text-[10px] uppercase tracking-wider text-green-700 font-bold">Teens notified</div>
              </div>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">PRD</div>
                <pre className="whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{draftResult?.scaffold?.prd_bullet}</pre>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">UX outline</div>
                <pre className="whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{draftResult?.scaffold?.ux_outline}</pre>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Component</div>
                <code className="block bg-gray-50 rounded-lg p-2 text-xs font-mono text-gray-800">
                  /app/frontend/src/components/dashboard/{draftResult?.scaffold?.component_filename}
                </code>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">API contract</div>
                <div className="text-xs text-gray-700">{draftResult?.scaffold?.api_contract}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">First-PR tasks</div>
                <ul className="list-disc pl-5 text-xs space-y-1">
                  {(draftResult?.scaffold?.first_pr_tasks || []).map((t, idx) => (
                    <li key={idx}>{t}</li>
                  ))}
                </ul>
              </div>
              <div className="text-[10px] text-gray-400 italic">
                Scaffold + skeleton appended to <code>{draftResult?.roadmap_appended_to}</code> · model {draftResult?.model}
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button
                type="button"
                onClick={() => setDraftResult(null)}
                className="px-5 py-2 rounded-full font-semibold text-sm text-white"
                style={{ background: '#7B4FD8' }}
                data-testid="draft-result-done"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceOfUserCard;
